import { PauseCircleOutlined, PlusCircleOutlined } from '@ant-design/icons';
import { createElement, useCallback, useMemo } from 'react';

import type { TimeFormat } from '@app/api/Branches/types';

import {
  calculateOverlapLayout,
  findFreeSegmentAt,
  formatTime,
  formatTimelineLabel,
  getCalendarPixelsPerMinute,
  getFreeSegments,
  toMinutes,
} from '@pages/Calendar/utils';
import type {
  CalendarAppointmentLayout,
  MinutesInterval,
  OverlapLayoutParams,
} from '@pages/Calendar/utils';

import type {
  CalendarBreak,
  CalendarEvent,
  CalendarSlotMenu,
  Employee,
} from '@pages/Calendar/data';

import { useActiveAppointmentHover } from '../../AppointmentCard/hooks';

import type { MouseEvent } from 'react';

const MIN_HEIGHT_FOR_NON_WORKING_TIME_LABEL = 28;

interface UseEmployeeColumnProps extends Employee {
  startTime: string;
  endTime: string;
  stepMinutes: number;
  intervalHeightPx: number;
  events: CalendarEvent[];
  // Влияет только на подпись слота: time/freeStartTime/freeEndTime остаются
  // в HH:mm — их разбирают обратно при создании записи (issue/11162).
  timeFormat?: TimeFormat | null;
  onSlotClick: (employeeId: string, time: string, endTime: string) => void;
  onSlotBreakClick?: (
    employeeId: string,
    time: string,
    endTime: string
  ) => void;
  onBreakClick?: (employeeId: string, calendarBreak: CalendarBreak) => void;
  // Открытое меню шага сетки страницы (issue/11214): колонка показывает его,
  // только если меню открыто в ней.
  activeSlotMenu: CalendarSlotMenu | null;
  onSlotMenuOpen: (slotMenu: CalendarSlotMenu) => void;
  onSlotMenuClose: () => void;
}

interface BookingSlot {
  time: string;
  topPx: number;
  heightPx: number;
  label: string;
  startMinutes: number;
  // Первый свободный отрезок шага — его показывает ховер.
  freeStartTime: string;
  freeEndTime: string;
  // Все свободные отрезки: запись или перерыв посередине шага делит его на
  // несколько, клик выбирает отрезок по своей позиции.
  freeSegments: MinutesInterval[];
  isOccupied: boolean;
  isNonWorking: boolean;
  isWorkingTimeSection: boolean;
  isNonWorkingTimeSection: boolean;
  isBreak: boolean;
  isInactiveWorkday: boolean;
  isBeforeBlock: boolean;
  isBlockStart: boolean;
  isBlockEnd: boolean;
  shouldShowNonWorkingTimeLabel: boolean;
}

export interface LayoutedEvent {
  event: CalendarEvent;
  layout: OverlapLayoutParams;
}

export interface BreakBlock {
  calendarBreak: CalendarBreak;
  topPx: number;
  heightPx: number;
}

export const useEmployeeColumn = ({
  startTime,
  endTime,
  stepMinutes,
  intervalHeightPx,
  onSlotClick,
  onSlotBreakClick,
  onBreakClick,
  events,
  workStart,
  workEnd,
  hasActiveWorkday = true,
  breaks,
  id,
  timeFormat,
  activeSlotMenu,
  onSlotMenuOpen,
  onSlotMenuClose,
}: UseEmployeeColumnProps) => {
  const timeSlots = useMemo<BookingSlot[]>(() => {
    const slots: BookingSlot[] = [];
    const startTotal = toMinutes(startTime);
    const endTotal = toMinutes(endTime);
    const workStartMinutes = toMinutes(workStart);
    const workEndMinutes = toMinutes(workEnd);
    const pixelsPerMinute = getCalendarPixelsPerMinute(intervalHeightPx);
    const sectionHeightPx = stepMinutes * pixelsPerMinute;
    const eventIntervals = events.map((event) => ({
      startMinutes: toMinutes(event.start),
      endMinutes: toMinutes(event.end),
    }));
    const breakIntervals = hasActiveWorkday
      ? breaks.map((calendarBreak) => ({
          startMinutes: toMinutes(calendarBreak.timeStart),
          endMinutes: toMinutes(calendarBreak.timeEnd),
        }))
      : [];
    // Шаг целиком под перерывами. Сам перерыв рисует точный по минутам
    // break-block, а шаг закрашивается, только если свободного времени в нём
    // нет — иначе короткий перерыв выглядел длиной в целый шаг (us/11381).
    const isCoveredByBreaks = (slotStartMinutes: number) =>
      getFreeSegments(
        slotStartMinutes,
        slotStartMinutes + stepMinutes,
        breakIntervals
      ).length === 0;

    for (let m = startTotal; m < endTotal; m += stepMinutes) {
      const slotTime = formatTime(m);
      const slotEndMinutes = m + stepMinutes;
      const prevSlotMinutes = m - stepMinutes;
      const nextSlotMinutes = m + stepMinutes;

      const isInactiveWorkday = !hasActiveWorkday;
      const isNonWorking =
        isInactiveWorkday || m < workStartMinutes || m >= workEndMinutes;
      const isPrevNonWorking =
        isInactiveWorkday ||
        (prevSlotMinutes >= startTotal &&
          (prevSlotMinutes < workStartMinutes ||
            prevSlotMinutes >= workEndMinutes));
      const isNextNonWorking =
        isInactiveWorkday ||
        (nextSlotMinutes < endTotal &&
          (nextSlotMinutes < workStartMinutes ||
            nextSlotMinutes >= workEndMinutes));

      const isBreak = !isNonWorking && isCoveredByBreaks(m);
      const isNextBreak =
        !isNextNonWorking &&
        nextSlotMinutes < endTotal &&
        isCoveredByBreaks(nextSlotMinutes);
      const isWorkingTimeSection = !isNonWorking && !isBreak;
      const isNonWorkingTimeSection = !isBreak && !isWorkingTimeSection;
      const isBeforeBlock =
        !isNonWorking && !isBreak && (isNextNonWorking || isNextBreak);
      // Границы перерыва рисует break-block, у шагов — только нерабочее время.
      const isBlockStart = isNonWorking && !isPrevNonWorking;
      const isBlockEnd = isNonWorking && !isNextNonWorking;

      // Записи и перерывы, задевающие шаг хотя бы частично, вычитаются из
      // шага: клик и ховер работают по оставшимся свободным отрезкам
      // (issue/11151). Перерывы учитываются только в рабочее время.
      const freeSegments = getFreeSegments(
        m,
        slotEndMinutes,
        isNonWorking ? eventIntervals : [...eventIntervals, ...breakIntervals]
      );
      const [firstFreeSegment] = freeSegments;
      const freeStartMinutes = firstFreeSegment?.startMinutes ?? slotEndMinutes;
      const freeEndMinutes = firstFreeSegment?.endMinutes ?? slotEndMinutes;

      const isOccupied = !isNonWorking && !isBreak && !firstFreeSegment;
      const shouldShowNonWorkingTimeLabel =
        isNonWorkingTimeSection &&
        sectionHeightPx >= MIN_HEIGHT_FOR_NON_WORKING_TIME_LABEL;

      slots.push({
        time: slotTime,
        topPx: (m - startTotal) * pixelsPerMinute,
        heightPx: sectionHeightPx,
        startMinutes: m,
        label: formatTimelineLabel(freeStartMinutes, timeFormat),
        freeStartTime: formatTime(freeStartMinutes),
        freeEndTime: formatTime(freeEndMinutes),
        freeSegments,
        isOccupied,
        isNonWorking,
        isWorkingTimeSection,
        isNonWorkingTimeSection,
        isBreak,
        isInactiveWorkday,
        isBeforeBlock,
        isBlockStart,
        isBlockEnd,
        shouldShowNonWorkingTimeLabel,
      });
    }

    return slots;
  }, [
    startTime,
    endTime,
    stepMinutes,
    events,
    workStart,
    workEnd,
    breaks,
    hasActiveWorkday,
    intervalHeightPx,
    timeFormat,
  ]);

  const breakBlocks = useMemo<BreakBlock[]>(() => {
    const startTotal = toMinutes(startTime);
    const endTotal = toMinutes(endTime);
    const pixelsPerMinute = getCalendarPixelsPerMinute(intervalHeightPx);

    if (!hasActiveWorkday) {
      return [];
    }

    return breaks
      .map((calendarBreak) => {
        const breakStartMinutes = toMinutes(calendarBreak.timeStart);
        const breakEndMinutes = toMinutes(calendarBreak.timeEnd);
        const clampedStart = Math.max(breakStartMinutes, startTotal);
        const clampedEnd = Math.min(breakEndMinutes, endTotal);

        return {
          calendarBreak,
          topPx: (clampedStart - startTotal) * pixelsPerMinute,
          heightPx: (clampedEnd - clampedStart) * pixelsPerMinute,
        };
      })
      .filter(({ heightPx }) => heightPx > 0);
  }, [breaks, startTime, endTime, intervalHeightPx, hasActiveWorkday]);

  const layoutedEvents = useMemo<LayoutedEvent[]>(() => {
    const startTotal = toMinutes(startTime);
    const pixelsPerMinute = getCalendarPixelsPerMinute(intervalHeightPx);

    const eventInputs = events.map((event) => {
      const eventStart = toMinutes(event.start);
      const eventEnd = toMinutes(event.end);

      return {
        id: event.id,
        startMinutes: eventStart,
        endMinutes: eventEnd,
        topPx: (eventStart - startTotal) * pixelsPerMinute,
        heightPx: (eventEnd - eventStart) * pixelsPerMinute,
      };
    });

    const layoutMap = calculateOverlapLayout(eventInputs);

    return events.map((event) => ({
      event,
      layout: layoutMap.get(event.id)!,
    }));
  }, [events, intervalHeightPx, startTime]);

  const appointmentLayouts = useMemo<CalendarAppointmentLayout[]>(
    () =>
      layoutedEvents.map(({ event, layout }) => {
        const startMinutes = toMinutes(event.start);
        const endMinutes = toMinutes(event.end);

        return {
          id: event.id,
          startMinutes,
          endMinutes,
          durationMinutes: endMinutes - startMinutes,
          topPx: layout.topPx,
          heightPx: layout.heightPx,
          leftPx: layout.leftPx,
          rightPx: layout.rightPx,
          zIndex: layout.zIndex,
          stackIndex: layout.stackIndex,
        };
      }),
    [layoutedEvents]
  );

  const { activeEventId, gridHoverHandlers } =
    useActiveAppointmentHover(appointmentLayouts);

  const slotMenu = useMemo(
    () => (activeSlotMenu?.columnId === id ? activeSlotMenu : null),
    [activeSlotMenu, id]
  );

  const handleSlotClick = useCallback(
    (
      { time, startMinutes, topPx, heightPx, freeSegments }: BookingSlot,
      event: MouseEvent<HTMLDivElement>
    ) => {
      const pixelsPerMinute = heightPx / stepMinutes;
      const { top } = event.currentTarget.getBoundingClientRect();
      const clickMinutes =
        startMinutes + (event.clientY - top) / pixelsPerMinute;
      const segment = findFreeSegmentAt(freeSegments, clickMinutes);

      if (!segment) return;

      // Меню встаёт у выбранного отрезка, а не у всего шага.
      onSlotMenuOpen({
        columnId: id,
        time,
        freeStartTime: formatTime(segment.startMinutes),
        freeEndTime: formatTime(segment.endMinutes),
        topPx: topPx + (segment.startMinutes - startMinutes) * pixelsPerMinute,
        heightPx: (segment.endMinutes - segment.startMinutes) * pixelsPerMinute,
      });
    },
    [id, onSlotMenuOpen, stepMinutes]
  );

  const closeSlotMenu = useCallback(() => {
    onSlotMenuClose();
  }, [onSlotMenuClose]);

  const slotMenuItems = useMemo(
    () => [
      {
        key: 'add-record',
        icon: createElement(PlusCircleOutlined),
        label: 'Добавить запись',
        onClick: () => {
          if (!slotMenu) return;

          onSlotMenuClose();
          onSlotClick(id, slotMenu.freeStartTime, slotMenu.freeEndTime);
        },
      },
      {
        key: 'add-break',
        icon: createElement(PauseCircleOutlined),
        label: 'Добавить перерыв',
        onClick: () => {
          if (!slotMenu) return;

          onSlotMenuClose();
          onSlotBreakClick?.(id, slotMenu.freeStartTime, slotMenu.freeEndTime);
        },
      },
    ],
    [id, onSlotBreakClick, onSlotClick, onSlotMenuClose, slotMenu]
  );

  const handleBreakClick = useCallback(
    (event: MouseEvent<HTMLDivElement>, calendarBreak: CalendarBreak) => {
      event.stopPropagation();
      onBreakClick?.(id, calendarBreak);
    },
    [id, onBreakClick]
  );

  return {
    timeSlots,
    layoutedEvents,
    breakBlocks,
    activeEventId,
    gridHoverHandlers,
    handleBreakClick,
    handleSlotClick,
    slotMenu,
    slotMenuItems,
    closeSlotMenu,
  };
};
